import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";
import { WORKSPACES, resolveActive } from "../../config/workspaces";

/**
 * Enterprise workspace-grouped sidebar (v2 · Feb 2026).
 *
 * Groups + items come from /src/config/workspaces.js so sidebar,
 * workspace-tab bar, and command palette stay in perfect sync.
 *
 * Persists per-user group open/close state in localStorage. Accordion by
 * default; users can opt into multi-expand via the `rf_nav_multi_expand`
 * flag.
 *
 * Responsive:
 *   • <md   full drawer, all labels visible
 *   • md    icon-rail, labels hidden
 *   • lg+   full sidebar with labels and group headers
 */

const STORAGE_KEY = (userId) => `rf_nav_open_v2__${userId || "anon"}`;
const MULTI_KEY   = "rf_nav_multi_expand";

function loadOpen(userId) {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY(userId)) || "null"); } catch { return null; }
}
function saveOpen(userId, state) {
  try { localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(state)); } catch { /* ignore */ }
}
function loadMulti() {
  try { return localStorage.getItem(MULTI_KEY) === "true"; } catch { return false; }
}


function Badge({ n }) {
  if (!n || n <= 0) return null;
  return (
    <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
          style={{ background: "#E50914", color: "#fff", lineHeight: "12px" }}
          data-testid="nav-badge">
      {n > 99 ? "99+" : n}
    </span>
  );
}


function NavItem({ item, badges, isCompact }) {
  const badge = item.badgeKey ? badges?.[item.badgeKey] : null;
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={!!item.exact}
      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""} ${isCompact ? "md:justify-center lg:justify-start" : ""}`}
      data-testid={item.testid}
      title={item.label}
    >
      <Icon size={16} weight="bold" />
      <span className={isCompact ? "md:hidden lg:inline" : ""}>{item.label}</span>
      <Badge n={badge} />
    </NavLink>
  );
}


function Group({ group, badges, open, onToggle, activeInGroup }) {
  const filtered = useMemo(
    () => group.items.filter((it) => !it.adminOnly || badges?.__isAdmin),
    [group.items, badges?.__isAdmin],
  );

  const contentRef = useRef(null);
  const [h, setH] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;
    const measure = () => { if (contentRef.current) setH(contentRef.current.scrollHeight); };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && contentRef.current) ro.observe(contentRef.current);
    return () => { if (ro) ro.disconnect(); };
  }, [filtered.length, open]);

  if (filtered.length === 0) return null;

  const badgeTotal = filtered.reduce(
    (s, it) => s + (it.badgeKey ? (badges?.[it.badgeKey] || 0) : 0), 0,
  );
  const Icon = group.icon;

  return (
    <div className="mb-0.5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`ws-panel-${group.id}`}
        data-testid={`nav-group-${group.id}`}
        className="w-full flex items-center gap-2 px-2 py-1.5 mt-1 rounded transition-colors md:hidden lg:flex"
        style={{
          background: activeInGroup ? "rgba(229, 9, 20, 0.08)" : "transparent",
          color: activeInGroup ? "#fff" : "#a3a3a3",
        }}
      >
        <Icon size={14} weight="bold" style={{ color: activeInGroup ? "#E50914" : "#737373" }} />
        <span className="label-tag flex-1 text-left tracking-widest text-[10px]">
          {group.label}
        </span>
        {badgeTotal > 0 && !open && <Badge n={badgeTotal} />}
        <CaretRight
          size={10} weight="bold"
          className="transition-transform duration-200"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0)" }}
        />
      </button>

      {/* md-only icon-rail divider */}
      <div className="hidden md:block lg:hidden h-px bg-neutral-800 my-2 mx-2" />

      <div
        id={`ws-panel-${group.id}`}
        role="region"
        aria-hidden={!open}
        className="overflow-hidden transition-[max-height,opacity] duration-200 ease-out md:max-lg:!max-h-none md:max-lg:!opacity-100"
        style={{ maxHeight: open ? `${h}px` : "0px", opacity: open ? 1 : 0 }}
      >
        <div ref={contentRef} className="flex flex-col gap-0.5 pl-0 lg:pl-2 pt-0.5 pb-1">
          {filtered.map((it) => (
            <NavItem key={it.to} item={it} badges={badges} isCompact />
          ))}
        </div>
      </div>
    </div>
  );
}


export default function SidebarNav({ userRole, userId, badges = {} }) {
  const location = useLocation();
  const multi = useMemo(() => loadMulti(), []);
  const badgesWithRole = useMemo(
    () => ({ ...badges, __isAdmin: userRole === "admin" }),
    [badges, userRole],
  );

  const visible = useMemo(
    () => WORKSPACES.filter((g) => !g.adminOnly || userRole === "admin"),
    [userRole],
  );

  const { workspace: activeWs } = resolveActive(location.pathname);
  const activeGroupId = activeWs?.id || null;

  const [openMap, setOpenMap] = useState(() => {
    const saved = loadOpen(userId);
    const init = {};
    if (saved) {
      for (const g of WORKSPACES) init[g.id] = !!saved[g.id];
    } else {
      for (const g of WORKSPACES) init[g.id] = false;
    }
    return init;
  });

  // When the route changes into a workspace, auto-open that workspace.
  useEffect(() => {
    if (!activeGroupId) return;
    setOpenMap((prev) => {
      if (prev[activeGroupId]) return prev;
      const next = multi
        ? { ...prev, [activeGroupId]: true }
        : Object.fromEntries(WORKSPACES.map((g) => [g.id, g.id === activeGroupId]));
      saveOpen(userId, next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId]);

  // Fresh session fallback: open Home by default.
  useEffect(() => {
    const anyOpen = Object.values(openMap).some(Boolean);
    if (!anyOpen && !activeGroupId) {
      const first = WORKSPACES.find((g) => g.defaultOpen);
      if (first) {
        setOpenMap((prev) => {
          const next = { ...prev, [first.id]: true };
          saveOpen(userId, next);
          return next;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) => {
    setOpenMap((prev) => {
      const willOpen = !prev[id];
      const next = multi
        ? { ...prev, [id]: willOpen }
        : (willOpen
            ? Object.fromEntries(WORKSPACES.map((g) => [g.id, g.id === id]))
            : { ...prev, [id]: false });
      saveOpen(userId, next);
      return next;
    });
  };

  return (
    <nav className="flex flex-col gap-0.5 px-2 md:px-1 lg:px-2 pb-2" data-testid="admin-sidebar-nav">
      {visible.map((g) => (
        <Group
          key={g.id}
          group={g}
          badges={badgesWithRole}
          open={!!openMap[g.id]}
          onToggle={() => toggle(g.id)}
          activeInGroup={activeGroupId === g.id}
        />
      ))}
    </nav>
  );
}
